import { getScenario, loadContent, SCENARIOS, type MapId } from '@orbomb/content';
import { planForRoom } from '../settings';
import type { RoomKind, Settings } from '../settings';
import {
  applyScenario,
  countdownRemainingTicks,
  createMatch,
  createSimConfig,
  regulationRemainingTicks,
  restoreState,
  step,
  type Camp,
  type MatchState,
  type SimConfig,
  type SimulationEvent,
  type TickInputs,
} from '@orbomb/sim';

export const CATALOGUE = SCENARIOS;

export class LocalSession {
  readonly mapId: MapId = 'arena';
  config: SimConfig;
  state: MatchState;
  seed: number;
  paused = true;
  roomKind: RoomKind | null = null;
  botOrange = false;
  botTurquoise = false;
  showHitboxes = false;
  showDebug = false;
  lastEvents: SimulationEvent[] = [];
  log: SimulationEvent[] = [];
  rematchDeclined = false;
  /** Set for a Supabase room. The host is TURQUOISE; the guest is ORANGE. */
  onlineCamp: Camp | null = null;
  resultNote = '';
  /** True while an online match cannot step the next tick yet. */
  waitingForPeer = false;
  /** F1 overlay: live net diagnostics. Empty when not in an online match. */
  debugNet = '';

  /** The camp the person at this screen plays: their online seat, or the human side against a bot. */
  get playerCamp(): Camp | null {
    if (this.onlineCamp !== null) return this.onlineCamp;
    if (this.botOrange === this.botTurquoise) return null;
    return this.botOrange ? 'TURQUOISE' : 'ORANGE';
  }

  constructor(seed = 1) {
    this.config = createSimConfig(loadContent(this.mapId));
    this.seed = seed;
    this.state = createMatch(this.config, { seed });
  }

  beginRoom(kind: RoomKind, settings: Settings): void {
    const plan = planForRoom(settings, kind);
    const seed = this.roomKind === null ? this.seed : this.seed + 1;
    this.roomKind = kind;
    this.onlineCamp = null;
    this.resultNote = '';
    this.waitingForPeer = false;
    this.botOrange = plan.orangeDriver === 'bot';
    this.botTurquoise = plan.turquoiseDriver === 'bot';
    this.reset(seed, false);
  }

  /** Shared seed from the room row. Ladder write is server-side; rematch stays available. */
  beginOnline(seed: number, camp: Camp): void {
    this.roomKind = null;
    this.onlineCamp = camp;
    this.botOrange = false;
    this.botTurquoise = false;
    this.resultNote = '';
    this.waitingForPeer = true;
    this.reset(seed, false);
    this.rematchDeclined = false;
    this.paused = false;
  }

  endOnline(): void {
    this.onlineCamp = null;
    this.resultNote = '';
    this.waitingForPeer = false;
    this.debugNet = '';
    this.rematchDeclined = false;
    this.paused = true;
  }

  reset(seed = this.seed, rematch = false): void {
    this.seed = seed;
    this.config = createSimConfig(loadContent(this.mapId));
    this.state = createMatch(
      this.config,
      rematch ? { seed, countdownTicks: this.config.ticks.rematchCountdown } : { seed },
    );
    this.lastEvents = [];
    this.log = [];
    this.paused = false;
    this.rematchDeclined = false;
  }

  loadScenario(id: string): void {
    const scenario = getScenario(id);
    this.config = createSimConfig(loadContent(scenario.map));
    this.seed = scenario.seed;
    this.state = applyScenario(this.config, scenario);
    this.lastEvents = [];
    this.log = [];
    this.paused = false;
    this.rematchDeclined = false;
  }

  advance(inputs: TickInputs): void {
    if (this.state.result) {
      this.lastEvents = [];
      return;
    }
    const { events } = step(this.state, this.config, inputs);
    this.lastEvents = events as SimulationEvent[];
    this.log.push(...events);
    if (this.log.length > 80) this.log.splice(0, this.log.length - 80);
  }

  /** Online prediction only. Sounds and HUD stay on the last server snapshot. */
  predict(inputs: TickInputs): void {
    if (this.state.result) return;
    step(this.state, this.config, inputs);
  }

  /** Server snapshot, then the live match may replay unacked local input. */
  applyServerState(state: MatchState, events: readonly SimulationEvent[]): void {
    this.state = restoreState(state, this.config);
    this.seed = state.seed;
    this.lastEvents = [...events];
    this.log.push(...events);
    if (this.log.length > 80) this.log.splice(0, this.log.length - 80);
    this.waitingForPeer = false;
    if (state.result) this.paused = true;
  }

  remainingClockTicks(): number {
    return regulationRemainingTicks(this.state);
  }

  countdownTicks(): number {
    return countdownRemainingTicks(this.state);
  }
}
